import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Shield, AlertTriangle, CheckCircle, FileText, Users, Building2 } from "lucide-react";
import { Button } from "../../components/ui/button";

const DefaillancesSecurisationMarchesPublics2025Article = () => {
  return (
    <>
      <Helmet>
        <title>Défaillances et Sécurisation des Marchés Publics en 2025 | Mon DCE Express</title>
        <meta name="description" content="Guide complet sur la prévention des défaillances d'entreprises dans les marchés publics et les nouvelles mesures de sécurisation en 2025. Garanties, assurances et bonnes pratiques." />
        <meta name="keywords" content="défaillances marchés publics, sécurisation appels d'offres, garanties marchés publics, assurance marché public, prévention défaillance entreprise" />
        <link rel="canonical" href="https://www.dce-express.fr/blog/defaillances-securisation-marches-publics-2025" />
      </Helmet>

      <article className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Link to="/blog" className="inline-flex items-center text-red-100 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Link>
            
            <div className="flex items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Sécurisation
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Guide 2025
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Défaillances et Sécurisation des Marchés Publics en 2025
            </h1>
            
            <p className="text-xl text-red-100 mb-6 leading-relaxed">
              Comment prévenir les risques de défaillance et sécuriser vos marchés publics : 
              garanties, assurances et nouvelles réglementations.
            </p>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-red-100">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Mis à jour en 2025
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                12 min de lecture
              </span>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              La défaillance d'une entreprise en cours de marché public peut avoir des conséquences 
              désastreuses pour l'acheteur public comme pour les autres acteurs économiques. 
              En 2025, de nouvelles mesures renforcent la sécurisation des marchés publics.
            </p>

            {/* Stats Box */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-8 my-8">
              <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Chiffres clés 2024-2025
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600">+15%</div>
                  <div className="text-sm text-red-700">Défaillances d'entreprises en 2024</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-600">62 000</div>
                  <div className="text-sm text-orange-700">Entreprises en difficulté</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-amber-600">8%</div>
                  <div className="text-sm text-amber-700">Des marchés publics impactés</div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Shield className="w-7 h-7 text-red-600" />
              1. Les principaux risques de défaillance
            </h2>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Types de défaillances rencontrées
            </h3>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                <span><strong>Redressement judiciaire :</strong> L'entreprise continue sous contrôle judiciaire mais peut poursuivre le marché</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                <span><strong>Liquidation judiciaire :</strong> Arrêt total de l'activité, nécessité de résilier le marché</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                <span><strong>Abandon de chantier :</strong> Cessation des travaux sans procédure officielle</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                <span><strong>Incapacité technique :</strong> L'entreprise ne peut plus honorer ses engagements</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Signaux d'alerte à surveiller
            </h3>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 my-6">
              <h4 className="font-semibold text-amber-800 mb-3">Indicateurs de fragilité financière :</h4>
              <ul className="space-y-2 text-amber-900">
                <li>• Retards répétés dans l'exécution des prestations</li>
                <li>• Demandes d'avances inhabituelles</li>
                <li>• Rotation importante du personnel</li>
                <li>• Difficultés à payer les sous-traitants</li>
                <li>• Privilèges et nantissements déclarés</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <FileText className="w-7 h-7 text-blue-600" />
              2. Les garanties obligatoires et facultatives
            </h2>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              La retenue de garantie
            </h3>

            <p>
              La retenue de garantie est prélevée sur chaque acompte versé au titulaire. Elle est plafonnée à 
              <strong> 5% du montant initial du marché</strong>. Elle peut être remplacée par une garantie à 
              première demande ou une caution personnelle et solidaire.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 my-6">
              <h4 className="font-semibold text-blue-800 mb-3">Conditions de libération :</h4>
              <ul className="space-y-2 text-blue-900">
                <li>✓ À l'expiration du délai de garantie (généralement 1 an)</li>
                <li>✓ Après levée des réserves éventuelles</li>
                <li>✓ Sur décision de l'acheteur dans les 30 jours suivant la demande</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              La garantie à première demande
            </h3>

            <p>
              C'est l'engagement d'un établissement financier de payer une somme déterminée, sur simple 
              demande de l'acheteur, sans pouvoir opposer d'exception. Elle offre une sécurité maximale 
              pour l'acheteur public.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              L'assurance décennale et responsabilité civile
            </h3>

            <p>
              Pour les marchés de travaux, l'assurance décennale est <strong>obligatoire</strong>. Elle couvre 
              les dommages compromettant la solidité de l'ouvrage pendant 10 ans après réception. L'assurance 
              responsabilité civile professionnelle couvre les dommages causés aux tiers.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <CheckCircle className="w-7 h-7 text-green-600" />
              3. Mesures de sécurisation en 2025
            </h2>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Nouvelles obligations réglementaires
            </h3>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-semibold text-green-800 mb-3">Pour les acheteurs</h4>
                <ul className="space-y-2 text-green-900 text-sm">
                  <li>• Vérification renforcée de la santé financière</li>
                  <li>• Consultation obligatoire du registre des entreprises</li>
                  <li>• Examen des références sur 5 ans minimum</li>
                  <li>• Clauses de résiliation anticipée</li>
                </ul>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <h4 className="font-semibold text-emerald-800 mb-3">Pour les entreprises</h4>
                <ul className="space-y-2 text-emerald-900 text-sm">
                  <li>• Déclaration sur l'honneur de non-défaillance</li>
                  <li>• Fourniture des 3 derniers bilans</li>
                  <li>• Attestation d'assurance à jour</li>
                  <li>• Information en cas de procédure collective</li>
                </ul>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Bonnes pratiques de sécurisation
            </h3>

            <ol className="space-y-4">
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
                <div>
                  <strong>Allotissement stratégique :</strong> Diviser le marché en lots réduit le risque 
                  de défaillance globale et favorise l'accès des PME.
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
                <div>
                  <strong>Avances encadrées :</strong> Limiter le versement d'avances ou exiger des garanties 
                  de restitution d'avance.
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
                <div>
                  <strong>Suivi régulier :</strong> Mettre en place des points d'étape et des indicateurs 
                  de suivi d'exécution.
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
                <div>
                  <strong>Clauses de substitution :</strong> Prévoir la possibilité de faire appel à un 
                  autre prestataire en cas de défaillance.
                </div>
              </li>
            </ol>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Users className="w-7 h-7 text-purple-600" />
              4. Que faire en cas de défaillance ?
            </h2>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Procédure à suivre
            </h3>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 my-6">
              <ol className="space-y-3 text-purple-900">
                <li><strong>1. Constater officiellement</strong> la défaillance (mise en demeure)</li>
                <li><strong>2. Notifier</strong> la résiliation du marché</li>
                <li><strong>3. Réaliser un état des lieux</strong> contradictoire</li>
                <li><strong>4. Mettre en œuvre</strong> les garanties financières</li>
                <li><strong>5. Organiser la continuité</strong> du service (marché de substitution)</li>
                <li><strong>6. Déclarer la créance</strong> auprès du mandataire judiciaire</li>
              </ol>
            </div>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Droits de l'acheteur public
            </h3>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Résiliation aux frais et risques du titulaire défaillant</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Exécution des garanties financières</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Exclusion de l'entreprise des futurs marchés (3 ans maximum)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Réclamation des surcoûts liés au marché de substitution</span>
              </li>
            </ul>

            {/* Conclusion Box */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-2xl p-8 my-12">
              <h3 className="text-xl font-bold mb-4">À retenir</h3>
              <p className="text-slate-300 leading-relaxed mb-4">
                La sécurisation des marchés publics passe par une analyse rigoureuse des candidatures, 
                la mise en place de garanties adaptées et un suivi attentif de l'exécution. Les nouvelles 
                mesures 2025 renforcent les outils de prévention pour limiter les risques de défaillance.
              </p>
              <p className="text-slate-300 leading-relaxed">
                En tant qu'entreprise candidate, démontrer votre solidité financière et votre fiabilité 
                est un atout majeur pour remporter des marchés publics.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-8 text-center text-white mt-12">
            <h3 className="text-2xl font-bold mb-4">
              Besoin d'aide pour vos candidatures ?
            </h3>
            <p className="text-red-100 mb-6 max-w-2xl mx-auto">
              Mon DCE Express vous accompagne dans l'analyse de vos dossiers de consultation 
              et vous aide à constituer des candidatures solides.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/analyse-gratuite">
                <Button size="lg" className="bg-white text-red-600 hover:bg-red-50">
                  Analyser un DCE gratuitement
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Voir les tarifs
                </Button>
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          <div className="mt-16">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Articles connexes</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/blog/comment-repondre-appel-offres" className="group">
                <div className="bg-white border border-slate-200 rounded-xl p-6 hover:border-red-300 hover:shadow-lg transition-all">
                  <h4 className="font-semibold text-slate-800 group-hover:text-red-600 mb-2">
                    Comment répondre à un appel d'offres
                  </h4>
                  <p className="text-sm text-slate-600">Guide complet pour candidater efficacement</p>
                </div>
              </Link>
              <Link to="/blog/criteres-attribution-marches-publics" className="group">
                <div className="bg-white border border-slate-200 rounded-xl p-6 hover:border-red-300 hover:shadow-lg transition-all">
                  <h4 className="font-semibold text-slate-800 group-hover:text-red-600 mb-2">
                    Critères d'attribution des marchés
                  </h4>
                  <p className="text-sm text-slate-600">Comprendre les critères de sélection</p>
                </div>
              </Link>
              <Link to="/blog/pme-marches-publics" className="group">
                <div className="bg-white border border-slate-200 rounded-xl p-6 hover:border-red-300 hover:shadow-lg transition-all">
                  <h4 className="font-semibold text-slate-800 group-hover:text-red-600 mb-2">
                    PME et marchés publics
                  </h4>
                  <p className="text-sm text-slate-600">Opportunités pour les petites entreprises</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default DefaillancesSecurisationMarchesPublics2025Article;
